const DEFAULT_SSO_DOMAINS = "anomalos.com,reedlab.com";

const COMPLETE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_SPLIT_RE = /[,\s]+/;
const ENTERPRISE_SSO_RE = /enterprise sso/i;
const PASSWORD_STRATEGY_RE = /password.*isn't allowed.*strategy/i;

export const ssoEmailDomains = (): string[] => {
  const raw =
    (import.meta.env.VITE_OKTA_EMAIL_DOMAINS as string | undefined) ??
    (import.meta.env.VITE_OKTA_EMAIL_DOMAIN as string | undefined) ??
    DEFAULT_SSO_DOMAINS;
  return raw
    .split(DOMAIN_SPLIT_RE)
    .map((domain) => domain.trim().toLowerCase())
    .filter(Boolean);
};

export const isEmployeeEmail = (email: string): boolean => {
  const domain = email.split("@")[1]?.toLowerCase();
  return Boolean(domain && ssoEmailDomains().includes(domain));
};

export const emailLooksComplete = (email: string): boolean =>
  COMPLETE_EMAIL_RE.test(email.trim());

export const hasEnterpriseSsoFactor = (
  factors: Array<{ strategy: string }> | null | undefined
): boolean =>
  (factors ?? []).some(
    (factor) =>
      factor.strategy === "enterprise_sso" || factor.strategy === "saml"
  );

export const isEnterpriseSsoError = (message: string): boolean =>
  ENTERPRISE_SSO_RE.test(message) || PASSWORD_STRATEGY_RE.test(message);

export const studioUrl = (): string =>
  (import.meta.env.VITE_STUDIO_URL as string | undefined) ??
  "https://studio.anomalos.com";

export const launcherUrl = (): string =>
  (import.meta.env.VITE_LAUNCHER_URL as string | undefined) ??
  "https://account.anomalos.com";

const REDIRECT_KEY = "anomalos.identity.redirect";

export const allowedOrigins = (): string[] =>
  [studioUrl(), launcherUrl()].map((url) => new URL(url).origin);

/**
 * Only absolute URLs on a known first-party origin are usable. A relative path
 * would resolve against this app's own origin, sending the user to a route that
 * does not exist here and bouncing them back to login — and any other absolute
 * URL would be an open redirect.
 */
export const safeRedirectTarget = (
  value: string | null,
  origins: string[] = allowedOrigins()
): string | null => {
  if (!value) {
    return null;
  }
  try {
    const target = new URL(value);
    return origins.includes(target.origin) ? target.toString() : null;
  } catch {
    return null;
  }
};

export const pickRedirect = (
  candidates: Array<string | null>,
  fallback: string,
  origins: string[] = allowedOrigins()
): string => {
  for (const candidate of candidates) {
    const safe = safeRedirectTarget(candidate, origins);
    if (safe) {
      return safe;
    }
  }
  return fallback;
};

export const rememberRedirect = (): void => {
  const params = new URLSearchParams(window.location.search);
  const target = pickRedirect(
    [params.get("redirect_url"), params.get("returnTo")],
    ""
  );
  if (target) {
    sessionStorage.setItem(REDIRECT_KEY, target);
  }
};

export const redirectAfterAuth = (): string => {
  const params = new URLSearchParams(window.location.search);
  const stored =
    typeof sessionStorage === "undefined"
      ? null
      : sessionStorage.getItem(REDIRECT_KEY);
  return pickRedirect(
    [params.get("redirect_url"), params.get("returnTo"), stored],
    studioUrl()
  );
};

export const authPath = (
  path: string,
  extra?: Record<string, string | undefined>
): string => {
  const params = new URLSearchParams();
  const current = new URLSearchParams(window.location.search);
  const redirect = current.get("redirect_url") ?? current.get("returnTo");
  if (redirect) {
    params.set("redirect_url", redirect);
  }
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value) {
        params.set(key, value);
      }
    }
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};
